import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import DatePicker from '@rio-cloud/rio-uikit/DatePicker';
import moment from 'moment';
import { useState } from 'react';
import type React from 'react';
import type { Moment } from 'moment';
import type { FreeTrialState } from '../api/freetrial/types';
import { useExtendFreeTrialAction } from './useExtendFreeTrialAction';

type FreeTrialExtensionDialogProps = {
    show: boolean;
    freeTrialState: FreeTrialState;
    statusLabel: React.ReactNode;
    onClose: () => void;
};

export function FreeTrialExtensionDialog(props: FreeTrialExtensionDialogProps) {
    const { show, freeTrialState, statusLabel, onClose } = props;
    const [extendEndsAt, setExtendEndsAt] = useState<Moment | null>(null);
    const [extendReason, setExtendReason] = useState('');
    const { extendFreeTrial, isLoading: isExtending } = useExtendFreeTrialAction();
    const currentFreeTrialEndDate =
        freeTrialState.endDate != null ? moment(freeTrialState.endDate, 'DD.MM.YYYY', true) : null;

    const resetDialog = () => {
        setExtendEndsAt(null);
        setExtendReason('');
    };

    const handleClose = () => {
        resetDialog();
        onClose();
    };

    const handleExtendDateChange = (value: string | Moment, isValid: boolean) => {
        if (isValid) {
            setExtendEndsAt(value as Moment);
        } else {
            setExtendEndsAt(null);
        }
    };

    const handleConfirmExtend = async () => {
        if (!extendEndsAt || extendReason.trim() === '') {
            return;
        }

        const wasSuccessful = await extendFreeTrial({
            accountId: freeTrialState.accountId,
            endsAt: extendEndsAt.format('YYYY-MM-DD'),
            reason: extendReason.trim(),
        });

        if (wasSuccessful) {
            handleClose();
        }
    };

    return (
        <ConfirmationDialog
            show={show}
            title='Confirm free trial extension'
            content={
                <div>
                    <div className='table-responsive'>
                        <table className='table table-hover table-head-filled table-bordered'>
                            <thead>
                                <tr>
                                    <th>AccountId</th>
                                    <th>Name</th>
                                    <th>Free Trial Status</th>
                                    <th>Current End Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{freeTrialState.accountId}</td>
                                    <td>{freeTrialState.accountName}</td>
                                    <td>{statusLabel}</td>
                                    <td>{freeTrialState.endDate ?? ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className='form-group margin-bottom-10'>
                        <label htmlFor='freeTrialExtendDate'>
                            <h5>New end date*</h5>
                        </label>
                        <DatePicker
                            inputProps={{
                                id: 'freeTrialExtendDate',
                                placeholder: 'Select date',
                            }}
                            dateFormat='DD.MM.YYYY'
                            timeFormat={false}
                            value={extendEndsAt || undefined}
                            onChange={handleExtendDateChange}
                            isValidDate={(current: Moment) => {
                                if (!currentFreeTrialEndDate || !currentFreeTrialEndDate.isValid()) {
                                    return false;
                                }
                                return current.isAfter(currentFreeTrialEndDate, 'day');
                            }}
                        />
                    </div>
                    <div className='form-group margin-bottom-10'>
                        <label htmlFor='freeTrialExtendReason'>
                            <h5>Reason or ticket number*</h5>
                        </label>
                        <ClearableInput
                            id='freeTrialExtendReason'
                            value={extendReason}
                            onChange={(newValue: string) => setExtendReason(newValue)}
                            placeholder='Enter reason or ticket number'
                        />
                    </div>
                </div>
            }
            cancelButtonText='Abort'
            confirmButtonText={isExtending ? 'Extending...' : 'Extend free trial'}
            onClickCancel={handleClose}
            onClickConfirm={handleConfirmExtend}
            disableConfirm={extendEndsAt === null || extendReason.trim() === '' || isExtending}
        />
    );
}
